import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Badge from "@mui/material/Badge";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import InsightsOutlined from "@mui/icons-material/InsightsOutlined";
import { DataGrid, type GridColDef, type GridRenderCellParams } from "@mui/x-data-grid";
import { PageHeader } from "@/components/PageHeader";
import { ForwardInboxCard } from "@/components/applications/ForwardInboxCard";
import { useAuth } from "@/hooks/useAuth";
import { useCandidate } from "@/context/CandidateContext";
import { useApplications } from "@/context/ApplicationContext";
import { classifyRejection, type RejectionInsight } from "@/server/classify-rejection";
import { supabase } from "@/integrations/supabase/client";
import {
  fetchForwardedEmails,
  forwardedEmailsQueryKey,
  extractCompany,
  extractRole,
  type ForwardedEmailRow,
} from "@/lib/queries/forwardedEmails";
import { toast } from "sonner";

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

export const Route = createFileRoute("/app/learning")({
  beforeLoad: () => {
    throw redirect({ to: "/app/tools", search: { tab: "rejection-insights" } });
  },
  component: () => null,
});

// ---------- Signal decoder ----------
type SignalTag =
  | "#Skill-Gap"
  | "#Experience-Level"
  | "#Culture-Fit"
  | "#Location"
  | "#Compensation"
  | "#Position-Filled"
  | "#Unspecified";

const TAG_COLORS: Record<
  SignalTag,
  "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"
> = {
  "#Skill-Gap": "error",
  "#Experience-Level": "warning",
  "#Culture-Fit": "secondary",
  "#Location": "info",
  "#Compensation": "primary",
  "#Position-Filled": "default",
  "#Unspecified": "default",
};

const KNOWN_SKILLS = [
  "React", "TypeScript", "JavaScript", "Next.js", "Node.js", "GraphQL",
  "Kubernetes", "Docker", "Rust", "Go", "Python", "AWS", "GCP", "Azure",
  "PostgreSQL", "Redis", "Kafka", "Terraform", "Svelte", "Vue", "Tailwind",
  "OSS", "Open Source", "Accessibility", "WebGL", "Three.js", "D3",
];

function decodeSignal(reason: string | null): {
  tags: SignalTag[];
  suggestedSkills: string[];
} {
  if (!reason) return { tags: ["#Unspecified"], suggestedSkills: [] };
  const tags: SignalTag[] = [];
  const checks: Array<[RegExp, SignalTag]> = [
    [/skill|experience with|proficien|knowledge of|familiar|internals/i, "#Skill-Gap"],
    [/senior|years of experience|seniority|\blead\b|principal|staff/i, "#Experience-Level"],
    [/culture|team fit|values/i, "#Culture-Fit"],
    [/location|remote|onsite|relocat/i, "#Location"],
    [/compensation|salary|budget/i, "#Compensation"],
    [/position filled|moved forward with|other candidate/i, "#Position-Filled"],
  ];
  for (const [re, tag] of checks) if (re.test(reason)) tags.push(tag);
  if (tags.length === 0) tags.push("#Unspecified");

  const suggestedSkills = KNOWN_SKILLS.filter((s) =>
    new RegExp(`\\b${s.replace(/[.+]/g, "\\$&")}\\b`, "i").test(reason),
  );

  return { tags, suggestedSkills };
}

function formatDate(iso: string) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}

function scoreColor(s: number | null): "success" | "primary" | "default" {
  if (s == null) return "default";
  if (s >= 8) return "success";
  if (s >= 6) return "primary";
  return "default";
}

export function RejectionInsightsPanel({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { profile, setVerifiedSkills } = useCandidate();
  const { currentStageFor } = useApplications();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [refactoring, setRefactoring] = useState(false);
  const [insightCache, setInsightCache] = useState<Record<string, RejectionInsight>>({});
  const [classifying, setClassifying] = useState<string | null>(null);
  const [classifyError, setClassifyError] = useState<string | null>(null);

  const { data: forwardedEmails = [] } = useQuery({
    queryKey: userId ? forwardedEmailsQueryKey(userId) : ["forwarded-emails", "anon"],
    queryFn: () => fetchForwardedEmails(userId as string),
    enabled: !!userId,
  });

  // Only emails the classifier flagged as a rejection. Body becomes the "reason".
  const rejected = useMemo(
    () =>
      forwardedEmails
        .filter((e: ForwardedEmailRow) => e.rejection_signal === "Rejected" && e.body_text)
        .map((e: ForwardedEmailRow) => ({
          id: e.id,
          applicationId: e.application_id,
          company: extractCompany(e.from_address, e.subject),
          role: extractRole(e.subject),
          appliedAt: e.received_at,
          matchScore: null as number | null,
          rejectionReason: e.body_text,
        })),
    [forwardedEmails],
  );

  // Lightweight regex decode for instant table rendering
  const decodedRows = useMemo(
    () =>
      rejected.map((a) => {
        const { tags, suggestedSkills } = decodeSignal(a.rejectionReason);
        return { ...a, tags, suggestedSkills, primaryTag: tags[0] };
      }),
    [rejected],
  );

  const selected = decodedRows.find((r) => r.id === selectedId) ?? null;
  const selectedInsight = selectedId ? insightCache[selectedId] : null;

  // Lazy classify on selection
  useEffect(() => {
    if (!selected || !selected.rejectionReason) return;
    if (insightCache[selected.id]) return;
    let cancelled = false;
    (async () => {
      setClassifying(selected.id);
      setClassifyError(null);
      try {
        const headers = await getAuthHeaders();
        const stage = selected.applicationId ? currentStageFor(selected.applicationId) : null;
        const result = await classifyRejection({
          data: {
            reason: selected.rejectionReason!,
            company: selected.company,
            role: selected.role,
            applicationId: selected.applicationId ?? selected.id,
            stageKey: stage?.stage_key ?? null,
          },
          headers,
        });
        if (!cancelled) {
          setInsightCache((prev) => ({ ...prev, [selected.id]: result }));
        }
      } catch (e) {
        if (!cancelled) {
          setClassifyError(e instanceof Error ? e.message : "Failed to classify rejection.");
        }
      } finally {
        if (!cancelled) setClassifying(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, insightCache]);

  // Aggregate suggestions across all rejections, dedupe against current skills
  const currentSkillNames = useMemo(
    () => new Set(profile.verifiedSkills.map((s) => s.name.toLowerCase())),
    [profile.verifiedSkills],
  );

  // Prefer AI insight skills; fall back to regex for unclassified rows
  const pendingAdditions = useMemo(() => {
    const set = new Set<string>();
    for (const r of decodedRows) {
      const ai = insightCache[r.id];
      if (ai) {
        for (const s of ai.suggestedSkills) {
          if (!currentSkillNames.has(s.name.toLowerCase())) set.add(s.name);
        }
      } else {
        for (const s of r.suggestedSkills) {
          if (!currentSkillNames.has(s.toLowerCase())) set.add(s);
        }
      }
    }
    return Array.from(set);
  }, [decodedRows, insightCache, currentSkillNames]);

  // Aggregate stats
  const stats = useMemo(() => {
    const total = rejected.length;
    const tagCounts = new Map<SignalTag, number>();
    let scoreSum = 0;
    let scoreN = 0;
    for (const r of decodedRows) {
      for (const t of r.tags) tagCounts.set(t, (tagCounts.get(t) ?? 0) + 1);
      if (r.matchScore != null) {
        scoreSum += r.matchScore;
        scoreN += 1;
      }
    }
    let topTag: SignalTag | null = null;
    let topCount = 0;
    for (const [t, c] of tagCounts) {
      if (c > topCount) {
        topCount = c;
        topTag = t;
      }
    }
    return {
      total,
      topTag,
      avgScore: scoreN ? scoreSum / scoreN : null,
    };
  }, [rejected, decodedRows]);

  async function handleRefactor() {
    if (pendingAdditions.length === 0) return;
    setRefactoring(true);
    const additions = pendingAdditions.map((name) => ({
      name,
      level: "beginner" as const,
      source: "rejection-feedback",
    }));
    setVerifiedSkills([...profile.verifiedSkills, ...additions]);
    setRefactoring(false);
    toast.success(
      `Added ${additions.length} skill${additions.length === 1 ? "" : "s"} to your profile based on ${rejected.length} rejection signal${rejected.length === 1 ? "" : "s"}.`,
    );
  }

  const columns: GridColDef[] = [
    { field: "company", headerName: "Company", flex: 1, minWidth: 130 },
    { field: "role", headerName: "Role", flex: 1.4, minWidth: 200 },
    {
      field: "appliedAt",
      headerName: "Applied",
      width: 130,
      valueFormatter: (value: string) => formatDate(value),
    },
    {
      field: "matchScore",
      headerName: "Success",
      width: 100,
      renderCell: (params: GridRenderCellParams<Record<string, unknown>, number | null>) =>
        params.value == null ? (
          <Chip label="—" size="small" variant="outlined" />
        ) : (
          <Chip
            label={params.value.toFixed(1)}
            size="small"
            color={scoreColor(params.value)}
            variant={scoreColor(params.value) === "default" ? "outlined" : "filled"}
          />
        ),
    },
    {
      field: "primaryTag",
      headerName: "Signal",
      width: 170,
      renderCell: (params: GridRenderCellParams<Record<string, unknown>, SignalTag>) => (
        <Chip
          label={params.value}
          size="small"
          color={TAG_COLORS[params.value as SignalTag]}
          variant="outlined"
        />
      ),
    },
    {
      field: "rejectionReason",
      headerName: "Reason",
      flex: 1.6,
      minWidth: 240,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap title={params.value ?? ""}>
          {params.value ?? "—"}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      {!hideHeader && (
        <PageHeader
          title="Rejection Insights"
          subtitle="Decode rejection signals and refactor your profile from real feedback."
        />
      )}

      <Box sx={{ mb: 3 }}>
        <ForwardInboxCard />
      </Box>

      <Grid container spacing={3}>
        {/* LEFT: stats + grid */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              {/* Toolbar */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                sx={{ alignItems: { sm: "center" }, justifyContent: "space-between", gap: 2, mb: 2 }}
              >
                <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                  <InsightsOutlined color="primary" />
                  <Typography variant="h6">Rejected Applications</Typography>
                  <Chip size="small" label={rejected.length} />
                </Stack>
                <Badge badgeContent={pendingAdditions.length} color="primary">
                  <Button
                    variant="contained"
                    startIcon={
                      refactoring ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <AutoFixHighOutlined />
                      )
                    }
                    disabled={refactoring || pendingAdditions.length === 0}
                    onClick={handleRefactor}
                  >
                    Refactor Profile & Portfolio
                  </Button>
                </Badge>
              </Stack>

              {/* Stats strip */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Total rejections
                    </Typography>
                    <Typography variant="h5">{stats.total}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Most common signal
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {stats.topTag ? (
                        <Chip
                          label={stats.topTag}
                          color={TAG_COLORS[stats.topTag]}
                          size="small"
                        />
                      ) : (
                        <Typography variant="h6" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Avg success score
                    </Typography>
                    <Typography variant="h5">
                      {stats.avgScore != null ? stats.avgScore.toFixed(1) : "—"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* DataGrid */}
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <DataGrid
                  rows={decodedRows}
                  columns={columns}
                  density="compact"
                  autoHeight
                  disableRowSelectionOnClick={false}
                  checkboxSelection={false}
                  pageSizeOptions={[10, 25]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  onRowClick={(params) => setSelectedId(String(params.id))}
                  rowSelectionModel={
                    selectedId
                      ? { type: "include", ids: new Set([selectedId]) }
                      : { type: "include", ids: new Set() }
                  }
                  slots={{
                    noRowsOverlay: () => (
                      <Stack sx={{ alignItems: "center", justifyContent: "center", height: "100%", py: 6 }}>
                        <Typography color="text.secondary">
                          No rejections yet — forward rejection emails to your inbox above.
                        </Typography>
                      </Stack>
                    ),
                  }}
                  sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT: side panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
            <CardContent>
              <Typography variant="overline" color="text.secondary">
                Decoded Rejection Signal
              </Typography>

              {!selected ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography color="text.secondary" variant="body2">
                    Select a row to see the decoded signal.
                  </Typography>
                </Box>
              ) : (
                <Stack sx={{ gap: 2, mt: 1 }}>
                  <Box>
                    <Typography variant="h6">{selected.company}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selected.role}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Detected signals {classifying === selected.id && "(AI analysing…)"}
                    </Typography>
                    <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                      {(selectedInsight?.signals.map((s) => `#${s}` as SignalTag) ?? selected.tags).map((t) => (
                        <Chip
                          key={t}
                          label={t}
                          size="small"
                          color={TAG_COLORS[t as SignalTag] ?? "default"}
                          variant="filled"
                        />
                      ))}
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Original rejection reason
                    </Typography>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, mt: 0.5, bgcolor: "background.default" }}
                    >
                      <Typography variant="body2">
                        {selected.rejectionReason ?? "No reason provided."}
                      </Typography>
                    </Paper>
                  </Box>

                  {classifyError && <Alert severity="error">{classifyError}</Alert>}

                  {selectedInsight?.summary && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        AI summary
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedInsight.summary}
                      </Typography>
                    </Box>
                  )}

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Suggested skill additions
                    </Typography>
                    {classifying === selected.id ? (
                      <Stack direction="row" sx={{ alignItems: "center", gap: 1, mt: 0.5 }}>
                        <CircularProgress size={14} />
                        <Typography variant="body2" color="text.secondary">
                          Generating suggestions…
                        </Typography>
                      </Stack>
                    ) : (() => {
                      const ai = selectedInsight?.suggestedSkills ?? [];
                      const newOnes = ai.length
                        ? ai.filter((s) => !currentSkillNames.has(s.name.toLowerCase()))
                        : selected.suggestedSkills
                            .filter((s) => !currentSkillNames.has(s.toLowerCase()))
                            .map((name) => ({ name, rationale: "" }));
                      if (newOnes.length === 0) {
                        return (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            No new skills detected — your profile already covers this signal.
                          </Typography>
                        );
                      }
                      return (
                        <Stack sx={{ flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                          {newOnes.map((s) => (
                            <Box key={s.name}>
                              <Chip label={`+ ${s.name}`} size="small" color="success" variant="outlined" />
                              {s.rationale && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  {s.rationale}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      );
                    })()}
                  </Box>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
